import { renderFields } from "@highnoteplatform/card-viewer";
import { Lock } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  type CardVisualizationDetailsResponse,
  useGetCardVisualizationDetailsPaymentMethodsPaymentMethodTypeProviderVisualizationDetailsGet,
} from "@/api-v3";
import { ErrorCard } from "@/components/error-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffectOnUpdate } from "@/hooks/use-effect-on-update";
import { parseError } from "@/lib/error-handler";
import { cn } from "@/lib/utils";

import { isCardStatusActive } from "../../utils/card-status";

import { CardArtwork } from "./card-artwork";
import { type CardViewerProps } from "./card-viewer";

// Highnote hands the card data to the CVV and expiry iframes once. One that
// loads after that hand-off stays blank, and onReady never fires.
export const READY_TIMEOUT_MS = 5_000;
export const MAX_RENDER_ATTEMPTS = 3;

export const HighnoteCardViewer = (props: CardViewerProps) => {
  const { card, isVisible, onRegisterHandler } = props;
  const { t } = useTranslation();
  const rendered = useRef(false);
  const unmounted = useRef(false);
  const readyTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isVisibleRef = useRef(isVisible);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      clearTimeout(readyTimeout.current);
    };
  }, []);

  const {
    data: highnoteConfigData,
    error: highnoteConfigError,
    isLoading: loadingHighnoteConfig,
    refetch: refetchHighnoteConfig,
  } = useGetCardVisualizationDetailsPaymentMethodsPaymentMethodTypeProviderVisualizationDetailsGet(
    card.card_type,
    card.provider,
    undefined,
    { query: { enabled: isCardStatusActive(card.status) } }
  );

  useEffectOnUpdate(() => {
    const renderCard = async (
      cardData: CardVisualizationDetailsResponse,
      attempt: number
    ) => {
      const cardEnvironment =
        cardData.env.toLowerCase() === "live" ? "live" : "test";
      let ready = false;
      const result = await renderFields({
        clientToken: cardData.signature,
        paymentCardId: cardData.card_id,
        environment: cardEnvironment,
        enableClipboard: false,
        maskedOnLoad: !isVisibleRef.current,
        onReady: () => {
          ready = true;
          clearTimeout(readyTimeout.current);
        },
        onError: (error) => {
          console.log(error);
        },
        elements: {
          cardNumber: {
            selector: `#${card.id}_cardNumber`,
            styles: { color: "white" },
          },
          cvv: { selector: `#${card.id}_cvv`, styles: { color: "white" } },
          expirationDate: {
            selector: `#${card.id}_expirationDate`,
            styles: { color: "white" },
          },
        },
      });

      onRegisterHandler(card.id, {
        toggleCardNumberMask: () => result.toggleCardNumberMask(),
      });

      if (ready || unmounted.current || attempt >= MAX_RENDER_ATTEMPTS) return;

      readyTimeout.current = setTimeout(() => {
        const rerender = async () => {
          await result.unmount();
          if (unmounted.current) return;
          await renderCard(cardData, attempt + 1);
        };
        rerender().catch((error: unknown) => {
          console.error(error);
        });
      }, READY_TIMEOUT_MS);
    };

    if (highnoteConfigData) {
      if (rendered.current) return;
      else rendered.current = true;
      renderCard(highnoteConfigData, 1).catch((error: unknown) => {
        console.error(error);
      });
    }
  }, [highnoteConfigData, onRegisterHandler, card.id]);

  const renderCard = () => {
    if (highnoteConfigError) {
      const { title, message } = parseError(highnoteConfigError);
      return (
        <ErrorCard
          title={t(title)}
          message={t(message)}
          buttonLabel={t("retry")}
          onButtonClick={() => refetchHighnoteConfig()}
          className="h-full"
        />
      );
    }

    if (loadingHighnoteConfig) {
      return <Skeleton className="rounded-lg h-full" />;
    }

    return (
      <div className="relative h-full bg-primary rounded-lg overflow-hidden">
        <CardArtwork provider={card.provider} />
        <div className="relative h-full flex flex-col justify-end py-2 px-4 [&>label]:uppercase [&_iframe]:h-7">
          <div id={`${card.id}_cardNumber`}></div>
          <div>
            <div className="relative">
              <div
                id={`${card.id}_cvv`}
                className={cn(!isVisible && "invisible")}
              ></div>
              {!isVisible && (
                <span
                  aria-hidden
                  className="px-0.5 absolute inset-0 flex items-center text-white font-semibold -tracking-widest pointer-events-none select-none"
                >
                  •••
                </span>
              )}
            </div>
            <div className="relative">
              <div
                id={`${card.id}_expirationDate`}
                className={cn(!isVisible && "invisible")}
              ></div>
              {!isVisible && (
                <span
                  aria-hidden
                  className="px-0.5 absolute inset-0 flex items-center text-white font-semibold -tracking-widest pointer-events-none select-none"
                >
                  ••/••
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "relative aspect-[1.55]",
        highnoteConfigError ? "overflow-visible" : "overflow-hidden"
      )}
    >
      {renderCard()}

      {card.status.toLowerCase() === "suspended" && (
        <div
          role="status"
          aria-label="Card locked"
          className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px] z-10 rounded-lg"
        >
          <div className="flex flex-col items-center text-white">
            <Lock className="w-10 h-10 mb-2" />
            <span className="text-sm font-semibold uppercase tracking-wide">
              {t("card_locked")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
